import { useState, useCallback } from "react";
import { omit } from "lodash";
import {
  AccountDetailsMin,
  PayloadAccountDetailsMin,
  AccountDetails,
  useUpdateAccountMutation,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { formatDate, toPayloadAccountDetailsMin } from "../../../../utils";
import { accountCashbackPayload } from "../../../../utils/cashback";
import { accountTypeMap } from "../../types";

export interface PanelState {
  isOpen: boolean;
  account?: PayloadAccountDetailsMin;
}

export interface UseAccountPanelReturn {
  panel: PanelState;
  openPanel: (account: AccountDetails) => void;
  closePanel: () => void;
  handleSubmit: (data: AccountDetailsMin) => Promise<void>;
}

export const useAccountPanel = (accountId: number): UseAccountPanelReturn => {
  const [panel, setPanel] = useState<PanelState>({
    isOpen: false,
    account: undefined,
  });
  const [updateAccount] = useUpdateAccountMutation();

  const openPanel = useCallback((account: AccountDetails) => {
    const payload = toPayloadAccountDetailsMin(account);
    setPanel({
      isOpen: true,
      account: payload,
    });
  }, []);

  const closePanel = useCallback(() => {
    setPanel({
      isOpen: false,
      account: undefined,
    });
  }, []);

  const handleSubmit = useCallback(
    async (data: AccountDetailsMin) => {
      try {
        const payload: PayloadAccountDetailsMin = {
          ...omit(
            data,
            "initialBalance",
            "cashbackMode",
            "cashbackRate",
            "cashbackMonthlyCap",
          ),
          initialBalance: Number(data.initialBalance),
          startDate: formatDate(data.startDate),
          accountType: accountTypeMap[data.accountType.value],
          ...accountCashbackPayload(
            accountTypeMap[data.accountType.value],
            data,
          ),
        };

        // Invalidates `Account`, so the detail query refetches automatically.
        await updateAccount({ accountId, payload }).unwrap();

        notify.success("Account updated successfully!");
        closePanel();
      } catch {
        // Error toast handled by errorToastMiddleware; keep the panel open.
      }
    },
    [accountId, closePanel, updateAccount],
  );

  return {
    panel,
    openPanel,
    closePanel,
    handleSubmit,
  };
};
